import { useCallback, useEffect, useRef, useState } from 'react';

const CHROME_RESUME_INTERVAL_MS = 10_000;

const VOICE_PREF_KEY = 'aiAssistantVoiceReplies';

function readVoicePref(): boolean {
  try {
    return window.localStorage.getItem(VOICE_PREF_KEY) !== 'off';
  } catch {
    return true;
  }
}

function writeVoicePref(enabled: boolean) {
  try {
    window.localStorage.setItem(VOICE_PREF_KEY, enabled ? 'on' : 'off');
  } catch {
    // Preference just won't survive a reload.
  }
}

interface Options {
  onStart?: () => void;
  onEnd?: () => void;
}

export const textToSpeechSupported = (): boolean => {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
};

const isDigit = (ch: string | undefined): boolean => ch !== undefined && ch >= '0' && ch <= '9';
const isSpace = (ch: string | undefined): boolean => ch === ' ' || ch === '\t' || ch === '\n';

// Return the portion of the text we feel is a complete sentence.
// Don't split things like 3.5 Million into 3 and 5 million.
export function completedPortion(text: string): string {
  let lastEnd = -1;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '\n') {
      lastEnd = i;
    } else if (ch === '.' || ch === '!' || ch === '?') {
      const next = text[i + 1];
      const atEnd = i === text.length - 1;
      if (isDigit(next)) continue;
      if (isSpace(next) || (atEnd && !(ch === '.' && isDigit(text[i - 1])))) lastEnd = i;
    }
  }
  return lastEnd === -1 ? '' : text.slice(0, lastEnd + 1);
}

const BLOCK_BREAK = /<\/(p|li|ul|ol|h[456]|blockquote|pre)>|<br\s*\/?>/gi;

export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html.replace(BLOCK_BREAK, '\n'), 'text/html');
  const text = doc.body.textContent || '';
  return text.replace(/[ \t]+/g, ' ').replace(/ *\n */g, '\n').trim();
}

// Native voice quality varies wildly by OS/browser, and the default is usually the robotic one. Most
// platforms ship a much better voice we can opt into instead: Edge's "… (Natural)" neural voices,
// Chrome's "Google …" network voices, and Apple's Siri / enhanced voices. Score English voices by
// those name hints and pick the best available; the platform default is the fallback.
const VOICE_HINTS: Array<[RegExp, number]> = [
  [/natural/i, 40],
  [/\bsiri\b/i, 38],
  [/google/i, 30],
  [/(enhanced|premium)/i, 26],
  [/samantha/i, 16],
];

export function pickBestVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const english = voices.filter(v => v.lang.toLowerCase().startsWith('en'));
  const pool = english.length ? english : voices;
  if (!pool.length) return null;

  const score = (v: SpeechSynthesisVoice): number => {
    let s = v.lang.toLowerCase() === 'en-us' ? 5 : 0;
    for (const [re, points] of VOICE_HINTS) if (re.test(v.name)) s += points;
    if (/microsoft (david|zira|mark)/i.test(v.name)) s -= 10; // legacy robotic voices
    return s;
  };

  return pool.reduce((best, v) => (score(v) > score(best) ? v : best), pool[0]);
}

export function useTextToSpeech({ onStart, onEnd }: Options = {}) {
  const [speaking, setSpeaking] = useState(false);
  const [enabled, setEnabled] = useState(readVoicePref);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const spokenRef = useRef('');
  const pendingRef = useRef(0);
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);

  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const enqueue = useCallback((text: string) => {
    if (!enabledRef.current || !textToSpeechSupported()) return;

    const speakable = text.trim();
    if (!speakable) return;

    const utterance = new SpeechSynthesisUtterance(speakable);
    // Lazily pick the best voice at speak time so we never depend on getVoices() having populated by
    // the time the hook mounted; the effect below upgrades it once the async list arrives.
    if (!voiceRef.current) voiceRef.current = pickBestVoice(window.speechSynthesis.getVoices());
    if (voiceRef.current) {
      utterance.voice = voiceRef.current;
      utterance.lang = voiceRef.current.lang;
    } else {
      utterance.lang = 'en-US';
    }

    const settle = () => {
      pendingRef.current = Math.max(0, pendingRef.current - 1);
      if (pendingRef.current === 0) {
        setSpeaking(false);
        onEndRef.current?.();
      }
    };
    utterance.onend = settle;
    utterance.onerror = settle;

    if (pendingRef.current === 0) {
      setSpeaking(true);
      onStartRef.current?.();
    }
    pendingRef.current += 1;
    window.speechSynthesis.speak(utterance);
  }, []);

  const feed = useCallback((fullHtml: string) => {
    const text = htmlToText(fullHtml);
    if (!text.startsWith(spokenRef.current)) return;

    const complete = completedPortion(text.slice(spokenRef.current.length));
    if (!complete) return;

    spokenRef.current += complete;
    enqueue(complete);
  }, [enqueue]);

  const finish = useCallback((finalHtml: string) => {
    const text = htmlToText(finalHtml);
    if (!text.startsWith(spokenRef.current)) return;

    const remainder = text.slice(spokenRef.current.length);
    spokenRef.current = text;
    enqueue(remainder);
  }, [enqueue]);

  const cancel = useCallback(() => {
    spokenRef.current = '';
    pendingRef.current = 0;
    if (textToSpeechSupported()) window.speechSynthesis.cancel();
    setSpeaking(false);
  }, []);

  function toggleEnabled() {
    const next = !enabled;
    setEnabled(next);
    writeVoicePref(next);
    if (!next) cancel();
  }

  // Unlock the audio channel synchronously inside a user gesture (the mic tap). iOS Safari is strict
  // about starting audio long after a gesture, so we prime now with
  // a silent utterance while the gesture is still fresh.
  const prime = useCallback(() => {
    if (!textToSpeechSupported()) return;
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(''));
  }, []);

  // getVoices() is empty until the browser loads the list (fires 'voiceschanged'), so pick now and
  // re-pick when it arrives — that's when the good "Natural"/"Google" voices usually show up.
  useEffect(() => {
    if (!textToSpeechSupported()) return;
    const refresh = () => {
      voiceRef.current = pickBestVoice(window.speechSynthesis.getVoices());
    };
    refresh();
    window.speechSynthesis.addEventListener('voiceschanged', refresh);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', refresh);
  }, []);

  // Chrome silently pauses synthesis after ~15s; nudging resume() while speaking keeps long replies
  // going. Harmless in other browsers.
  useEffect(() => {
    if (!speaking || !textToSpeechSupported()) return;
    const id = setInterval(() => window.speechSynthesis.resume(), CHROME_RESUME_INTERVAL_MS);
    return () => clearInterval(id);
  }, [speaking]);

  useEffect(() => {
    return () => {
      if (textToSpeechSupported()) window.speechSynthesis.cancel();
    };
  }, []);

  return { cancel, enabled, feed, finish, prime, speaking, toggleEnabled };
}
